import React, { useState, useEffect, useCallback } from 'react';
import { FileDown, RefreshCw } from 'lucide-react';
import { useProtocoloSession } from '../../../../hooks/useProtocoloSession';
import { useOPUSession } from '../../../../hooks/useOPUSession';
import { useTESession } from '../../../../hooks/useTESession';
import { useDGSession } from '../../../../hooks/useDGSession';
import {
  fetchTextosDeVisita, salvarTextoDeVisita, apagarTextoDeVisita,
  montarTextoDeVisita, textosPorNumero,
} from '../../../../data/relatoriosVisita';
import { listClients } from '../../../../data/clients';
import { listServices } from '../../../../data/services';
import { fetchDoadorasDosEmbrioes } from '../../../../data/embryos';
import {
  RELATORIOS_DE_VISITA, gerarRelatorioDeVisita, requisitosDoRelatorio,
  avisosDoCabecalho, dataDoRelatorio, RELATORIO_POR_NUMERO, opuHerdada,
} from '../../../../utils/relatorioVisitaPDF';
import { MARK } from '../../../../constants';

/**
 * Os oito relatórios de visita do SEBRAE, num lugar só.
 *
 * Uma aba e não oito botões espalhados pelas etapas: os oito dividem o mesmo
 * cabeçalho e o mesmo número de projeto, e quem monta a pasta do programa
 * imprime os oito de uma vez, não um por semana.
 *
 * O texto de cada um vem calculado das etapas já lançadas. Os dois campos
 * ficam editáveis porque o relatório é documento de visita: o consultor
 * escreve o que aconteceu naquele dia, e nem tudo que aconteceu está numa
 * tabela do sistema.
 */
const RelatoriosVisitaTab = ({ service, laboratory, clients = [] }) => {
  const laboratoryId = service?.laboratory_id;
  const { loadProtocoloData } = useProtocoloSession(laboratoryId);
  const { loadOPUData } = useOPUSession(laboratoryId);
  const { loadTEData } = useTESession(laboratoryId);
  const { loadDGData } = useDGSession(laboratoryId);

  const [ctx, setCtx] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [rascunhos, setRascunhos] = useState({});
  const [salvando, setSalvando] = useState(null);

  /**
   * Junta tudo que entra nos oito relatórios num contexto só.
   *
   * As leituras vão juntas porque um relatório sozinho não diz nada: quem abre
   * esta aba quer ver de quais dos oito já dá para imprimir.
   *
   * O CLIENTE e o SERVIÇO são relidos aqui, não herdados das props. Metade do
   * cabeçalho mora neles — município, produtor, nº do projeto —, e eles são
   * editados em outra tela. Sem reler, preencher a cidade do cliente e voltar
   * aqui não mudava nada: o aviso continuava e o relatório saía com travessão
   * até o app inteiro recarregar.
   */
  /**
   * Serviço que herdou a OPU ou a FIV não tem OPU própria: ela está na
   * origem. Entram só as doadoras dos embriões transferidos neste serviço.
   */
  const opuDaOrigem = async (servico, te) => {
    if (!servico?.source_service_id || !['opu', 'fiv'].includes(servico.source_stage)) return null;
    const origem = await loadOPUData(servico.source_service_id);
    if (!origem) return null;
    const ids = (te?.transfers || [])
      .filter(t => t.status === 'APROVADA' && t.embryo_id)
      .map(t => t.embryo_id);
    const embrioes = await fetchDoadorasDosEmbrioes(ids);
    return opuHerdada(origem, (embrioes || []).map(e => e.donor_id));
  };

  const carregar = useCallback(async () => {
    if (!service?.id || !laboratoryId) return;
    setCarregando(true);
    setErro(null);
    try {
      const [protocolo, opu, te, dg, textos, clientesFrescos, servicosFrescos] =
        await Promise.all([
          loadProtocoloData(service.id),
          loadOPUData(service.id),
          loadTEData(service.id),
          loadDGData(service.id),
          fetchTextosDeVisita(service.id),
          listClients(laboratoryId),
          listServices(laboratoryId),
        ]);

      // A prop é a reserva: sem rede, `cached` devolve a cópia local e a lista
      // pode não trazer a linha. Melhor o dado da abertura que nenhum.
      const servico = (servicosFrescos || []).find(s => s.id === service.id) || service;
      const cliente = (clientesFrescos || []).find(c => c.id === servico.client_id)
        || clients.find(c => c.id === service.client_id) || null;

      setCtx({
        service: servico,
        laboratory,
        client: cliente,
        opu: opu || await opuDaOrigem(servico, te),
        te,
        dg30: dg?.dg30 || null,
        dg60: dg?.dg60 || null,
        protocolo,
        textos: textosPorNumero(textos),
      });
    } catch (e) {
      console.error('RelatoriosVisitaTab.carregar:', e);
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id, laboratoryId]);

  useEffect(() => { carregar(); }, [carregar]);

  const textoAtual = (numero, campo) => {
    const rascunho = rascunhos[numero]?.[campo];
    if (rascunho !== undefined) return rascunho;
    const gravado = ctx?.textos?.[numero]?.[campo];
    if (gravado) return gravado;
    const spec = RELATORIO_POR_NUMERO[numero];
    const padrao = campo === 'atividades'
      ? spec?.atividadesPadrao?.(ctx) : spec?.observacoesPadrao?.(ctx);
    return padrao || '';
  };

  /**
   * Devolve o relatório ao texto calculado dos dados.
   *
   * Descarta o rascunho e, havendo linha gravada, apaga a linha: enquanto ela
   * existir o texto continua congelado no número de embriões do dia em que
   * alguém salvou, mesmo depois de a etapa mudar.
   */
  const voltarAoCalculado = async (numero) => {
    const gravado = ctx?.textos?.[numero];
    setRascunhos(a => ({ ...a, [numero]: undefined }));
    if (!gravado?.id) return;
    setSalvando(numero);
    try {
      await apagarTextoDeVisita(gravado.id, service.id);
      await carregar();
    } catch (e) {
      console.error('RelatoriosVisitaTab.voltarAoCalculado:', e);
      setErro(e.message);
    } finally {
      setSalvando(null);
    }
  };

  const alterar = (numero, campo, valor) =>
    setRascunhos(a => ({ ...a, [numero]: { ...a[numero], [campo]: valor } }));

  const salvarTexto = async (numero) => {
    setSalvando(numero);
    try {
      const gravado = ctx?.textos?.[numero];
      await salvarTextoDeVisita(montarTextoDeVisita({
        id: gravado?.id,
        serviceId: service.id,
        laboratoryId,
        numero,
        dataVisita: textoAtual(numero, 'data_visita') || gravado?.data_visita,
        atividades: textoAtual(numero, 'atividades'),
        observacoes: textoAtual(numero, 'observacoes'),
      }));
      await carregar();
      setRascunhos(a => ({ ...a, [numero]: undefined }));
    } catch (e) {
      console.error('RelatoriosVisitaTab.salvarTexto:', e);
      setErro(e.message);
    } finally {
      setSalvando(null);
    }
  };

  if (carregando) {
    return <div className="p-8 text-center text-sm text-slate-500">Carregando etapas...</div>;
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold text-slate-900 text-sm">Relatórios de visita</h4>
          {/* Do contexto, não da prop: é o que o botão acabou de reler. */}
          <p className="text-xs text-slate-500 mt-0.5">
            Nº do projeto: <strong>{ctx?.service?.sebrae_projeto || '—'}</strong>
            {' · '}Embriões contratados: <strong>{ctx?.service?.embrioes_contratados ?? '—'}</strong>
          </p>
        </div>
        <button
          onClick={carregar}
          className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Recarregar dados
        </button>
      </div>

      {avisosDoCabecalho(ctx).length > 0 && (
        <div className="text-sm text-slate-700 bg-slate-50 border border-slate-300 rounded-lg px-3 py-2">
          <p className="mb-1">
            <span aria-hidden="true">{MARK.WARN}</span>{' '}
            Estes campos do cabeçalho vão sair em branco nos oito relatórios:
          </p>
          <ul className="list-disc list-inside text-xs text-slate-600">
            {avisosDoCabecalho(ctx).map(a => <li key={a}>{a}</li>)}
          </ul>
          {/* Fecha o ciclo: sem isto a pessoa preenche e volta sem entender
              por que o aviso continua. */}
          <p className="text-xs text-slate-500 mt-1">
            Depois de preencher, use <strong>Recarregar dados</strong> aqui em cima.
          </p>
        </div>
      )}

      {erro && (
        <p className="text-sm text-slate-700 bg-slate-50 border border-slate-300 rounded-lg px-3 py-2">
          <span aria-hidden="true">{MARK.WARN}</span> {erro}
        </p>
      )}

      {RELATORIOS_DE_VISITA.map(spec => {
        const faltas = requisitosDoRelatorio(spec.numero, ctx);
        const bloqueado = faltas.length > 0;
        const editado = rascunhos[spec.numero] !== undefined;
        // Texto gravado vence o calculado — e congela. Quem olha a aba precisa
        // ver que aquele relatório parou de acompanhar os dados.
        const fixo = !!ctx?.textos?.[spec.numero];

        return (
          <div key={spec.numero} className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {spec.numero}. {spec.etapa}
                </p>
                <p className="text-xs text-slate-500">
                  {dataDoRelatorio(spec.numero, ctx)
                    ? `Visita de ${dataDoRelatorio(spec.numero, ctx).split('-').reverse().join('/')}`
                    : 'Sem data'}
                  {bloqueado && ` · ${faltas.join(' · ')}`}
                </p>
              </div>
              <button
                onClick={() => gerarRelatorioDeVisita(spec.numero, ctx)}
                disabled={bloqueado}
                title={bloqueado ? faltas.join(' · ') : 'Abre a janela de impressão'}
                className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <FileDown className="w-3.5 h-3.5" /> Gerar
              </button>
            </div>

            {!bloqueado && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Atividades desenvolvidas
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                    value={textoAtual(spec.numero, 'atividades')}
                    onChange={(e) => alterar(spec.numero, 'atividades', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Observações
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                    value={textoAtual(spec.numero, 'observacoes')}
                    onChange={(e) => alterar(spec.numero, 'observacoes', e.target.value)}
                  />
                </div>
              </div>
            )}

            {(editado || fixo) && (
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                {editado && (
                  <button
                    onClick={() => salvarTexto(spec.numero)}
                    disabled={salvando === spec.numero}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {salvando === spec.numero ? 'Salvando...' : 'Salvar texto'}
                  </button>
                )}
                <button
                  onClick={() => voltarAoCalculado(spec.numero)}
                  disabled={salvando === spec.numero}
                  className="text-xs text-slate-600 hover:text-slate-900 underline disabled:opacity-50"
                >
                  Voltar ao texto calculado
                </button>
                {fixo && !editado && (
                  <span className="text-xs text-slate-500">
                    Texto fixo — não acompanha mais as etapas.
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RelatoriosVisitaTab;
